import React, { useState, Fragment } from "react";
import { styled } from "@mui/material/styles";
import { Card, Skeleton, Typography, Chip } from "@mui/material";

import AnimateOnClick from "@common/animations/AnimateOnClick";
import DelayedButton from "@common/DelayedButton";
import ProductPricingContainer from "@common/product/ProductPricingContainer";

import useShopContext from "@hooks/useShopContext";
import { getPercentageDiscount } from "@common/utils/productUtils";
import { prefixCurrency } from "@common/utils/currency";

const PREFIX = "VariantCard";

const classes = {
  imageContainer: `${PREFIX}-imageContainer`,
  image: `${PREFIX}-image`,
  imageSkeleton: `${PREFIX}-imageSkeleton`,
  variantDescriptionContainer: `${PREFIX}-variantDescriptionContainer`,
  discountLabel: `${PREFIX}-discountLabel`,
  soldOutLabel: `${PREFIX}-soldOutLabel`,
};

const StyledCard = styled(Card)(({ theme, soldout }) => ({
  display: "flex",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: theme.bordered ? `2px solid ${theme.palette.secondary.main}` : "none",
  boxShadow: theme.bordered ? "none" : theme.shadows[2],
  borderRadius: 5,
  width: "100%",
  marginLeft: "auto",
  marginRight: "auto",
  position: "relative",
  "&:hover": {
    cursor: "pointer",
  },
  gap: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },

  [`& .${classes.imageContainer}`]: {
    width: "39%",
    paddingTop: "39%",
    opacity: soldout ? 0.4 : 1,
    height: 0,
    objectFit: "contain",
    position: "relative",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      paddingTop: "100%",
    },
  },

  [`& .${classes.image}`]: {
    position: "absolute",
    top: "0",
    objectFit: "cover",
    width: "100%",
    height: "100%",
  },

  [`& .${classes.imageSkeleton}`]: {
    position: "absolute",
    top: "0",
    width: "100%",
    height: "100%",
  },

  [`& .${classes.variantDescriptionContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "60%",
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },

  // image: {
  //   width: "39%",
  //   height: 200,
  //   objectFit: "cover",
  //   margin: "auto 0",
  //   [theme.breakpoints.down("md")]: {
  //     height: 110,
  //     // paddingTop: "46%",
  //   },
  // },
  [`& .${classes.discountLabel}`]: {
    height: "16px",
    fontSize: "0.75rem",
    "& .MuiChip-label": {
      padding: "0px 8px",
    },
  },

  [`& .${classes.soldOutLabel}`]: {
    position: "absolute",
    right: 0,
    bottom: theme.spacing(3),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    padding: theme.spacing(0.5, 1),
  },
}));

const VariantCard = ({ variant, index, handleSelection }) => {
  const { vendor } = useShopContext();

  const [imageLoaded, setImageLoaded] = useState(false);

  const available =
    variant.inventory === null || variant.inventory === undefined || variant.inventory > 0;

  return (
    <AnimateOnClick key={index}>
      <DelayedButton
        onClick={() => {
          if (available) {
            handleSelection(variant);
          }
        }}
      >
        <StyledCard
          elevation={0}
          sx={{
            background: (theme) =>
              available ? theme.palette.common.white : theme.palette.grey[200],
          }}
          soldout={!available ? "true" : undefined}
        >
          <div className={classes.imageContainer}>
            {!imageLoaded && <Skeleton className={classes.imageSkeleton} variant="rectangular" />}
            <img
              alt={variant.title}
              style={{ display: imageLoaded ? "block" : "none", opacity: available ? 1 : 0.4 }}
              className={classes.image}
              src={variant.product_image}
              onLoad={() => setImageLoaded(true)}
            />
          </div>
          <div className={classes.variantDescriptionContainer}>
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {variant.title}
            </Typography>
            <ProductPricingContainer>
              <Typography variant="body1">{prefixCurrency(variant.price, vendor)}</Typography>
              {variant.original_price && (
                <Typography variant="body1" sx={{ textDecoration: "line-through" }}>
                  {prefixCurrency(variant.original_price, vendor)}
                </Typography>
              )}
              {variant.original_price && variant.show_percentage_discount && (
                <Chip
                  label={getPercentageDiscount(variant.original_price, variant.price) + `%`}
                  className={classes.discountLabel}
                />
              )}
            </ProductPricingContainer>

            {variant.inventory != null ? (
              <Typography variant="body2" color="textSecondary">
                <i>{variant.inventory > 0 ? `${variant.inventory} item(s) left` : "Sold out"}</i>
              </Typography>
            ) : (
              <Fragment />
            )}

            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              {variant.description}
            </Typography>
          </div>
          {!available ? (
            <Typography className={classes.soldOutLabel} variant="body2">
              Sold out
            </Typography>
          ) : null}
        </StyledCard>
      </DelayedButton>
    </AnimateOnClick>
  );
};

export default VariantCard;
